import { ServiceLayout } from "@/components/ServiceLayout"
import { intelligentDigitalExperiencesFAQs } from "@/data/faqData"

const IntelligentDigital = () => {
  return (
    <ServiceLayout
      title="Intelligent Digital Experiences"
      description="Creating responsive, personalization-first web ecosystems that leverage real-time data to adapt interfaces for every user interaction."
      heroImage="https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=2600"
      faqs={intelligentDigitalExperiencesFAQs}
      faqTitle="Intelligent Digital Experience FAQs"
      faqSubtitle="Common questions about AI-powered personalization and digital experiences"
      features={[
        {
          title: "Dynamic Personalization",
          description:
            "Websites that change layout, content, and calls-to-action based on visitor demographics, past behavior, and real-time intent.",
        },
        {
          title: "Headless CMS Architecture",
          description:
            "Decoupled frontend and backend systems allowing for omnichannel content delivery and lightning-fast page loads.",
        },
        {
          title: "Conversion Rate Optimization (CRO) AI",
          description:
            "Automated A/B testing agents that continuously refine UI elements to maximize user engagement and sales.",
        },
      ]}
      approach={{
        title: "Fluid Interface Architecture",
        description:
          "Treating the portal not as a static website, but as a rigid operating system for optimal work.",
        image:
          "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=2600",
        content: [
          "Modern enterprise portals often suffer from 'Dashboard Fatigue'. Our approach treats the portal not as a static website, but as a dynamic Operating System for work. We utilize Micro-Frontend (MFE) architectures to decompose monolithic legacy systems into independently deployable widgets, allowing different teams to ship features continuously without breaking the core platform.",
          "We implement a 'Federated Semantic Search' core that indexes data across your entire ecosystem - SharePoint, Salesforce, Jira, and internal Wikis. This allows users to ask natural language questions and retrieve answers instantly, bypassing the need to navigate complex menu structures.",
          "To ensure adoption, we embed a 'Digital Adoption Layer' that uses behavioral analytics to guide users through new workflows in real-time. The interface rearranges itself based on role frequency usage, surfacing the most critical tools for a CFO differently than for a Field Engineer.",
        ],
      }}
      benefits={[
        "3x Increase in User Engagement",
        "SEO-First Semantic Structure",
        "< 1s First Contentful Paint",
        "GDPR/CCPA Compliance Built-in",
        "Scalable Micro-Frontend Architecture",
        "Integrated Customer Data Platform (CDP)",
      ]}
      process={{
        steps: [
          {
            title: "User Journey Mapping",
            description:
              "Identifying key touchpoints and friction areas to design a seamless narrative flow for visitor conversion.",
          },
          {
            title: "Component Design System",
            description:
              "Building a library of reusable, accessible UI components to ensure brand consistency across all digital properties.",
          },
          {
            title: "Headless Implementation",
            description:
              "Developing the frontend experience decoupled from the CMS, enabling faster performance and omni-channel publishing.",
          },
          {
            title: "Personalization Setup",
            description:
              "Configuring rules and AI models to serve dynamic content segments based on real-time user data.",
          },
        ],
      }}
      techStack={[
        {
          category: "Frontend",
          items: [
            "Next.js",
            "React",
            "TypeScript",
            "Tailwind CSS",
            "Framer Motion",
          ],
        },
        {
          category: "CMS & Content",
          items: ["Sanity.io", "Contentful", "Strapi"],
        },
        {
          category: "Infrastructure",
          items: ["Vercel", "Netlify", "AWS CloudFront"],
        },
      ]}
    />
  )
}

export default IntelligentDigital
